import { AxiosError } from 'axios'

import { api } from '../libs/axios'

interface UserBaseData {
  id: string
  name: string
  avatar_url: string | null
}

export interface Store {
  id: string
  name: string
  city: string
  state: string
  seller_id: string
  supervisor_id: string
  trade_marketing_coordinator_id: string
  store_group_id: string
  created_at: string
  seller: UserBaseData,
  supervisor: UserBaseData,
  store_promoter: {
    promoter: UserBaseData
  }[],
  sellers_list: {
    seller: UserBaseData
  }[],
  group: {
    id: string
    name: string
    logo_url: string
  },
  promoters_company_id: string
}

export interface GetStoresRequest {
  page?: number
  id?: string
  name?: string
  state?: string
  city?: string
  seller_id?: string
  supervisor_id?: string
  promoter_id?: string
  promoters_company_id?: string
}

interface GetStoresResponse {
  stores: Store[]
  totalCount: number
  pageQty: number
}

export async function getStores(
  searchParams: GetStoresRequest,
): Promise<GetStoresResponse> {
  try {
    const params: { [key: string]: string | boolean } = {}

    Object.entries(searchParams).forEach(([key, value]) => {
      const param = key.replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase()
      params[param] = value
    })

    const result = await api.get<GetStoresResponse>('/stores', {
      params,
    })

    const { data } = result

    return data
  } catch (err) {
    if (err instanceof AxiosError) {
      console.error(err.message)
    }
    return {
      stores: [],
      pageQty: 0,
      totalCount: 0,
    }
  }
}

interface CreateStoreRequest {
  name: string
  city: string
  state: string
  store_group_id: string
  seller_id?: string | null
  supervisor_id?: string
  trade_marketing_coordinator_id?: string
  promoter_ids?: string[]
  seller_ids?: string[]
  promoters_company_id?: string | null
}

interface CreateStoreResponse {
  id: string
  name: string
}

export async function createStore({
  name,
  city,
  state,
  store_group_id,
  seller_id,
  supervisor_id,
  trade_marketing_coordinator_id,
  promoter_ids,
  seller_ids,
  promoters_company_id,
}: CreateStoreRequest) {
  try {
    const { data } = await api.post<CreateStoreResponse>('/stores', {
      name,
      city,
      state,
      store_group_id,
      seller_id,
      supervisor_id,
      trade_marketing_coordinator_id,
      promoter_ids,
      seller_ids,
      promoters_company_id,
    })

    return {
      store: data,
      success: true,
      message: 'Loja criada com sucesso!',
    }
  } catch (err) {
    if (err instanceof AxiosError) {
      return {
        success: false,
        message: err.response?.data.message || 'Não foi possível criar loja!',
      }
    }
  }
}

interface UpdateStoreRequest {
  storeId: string
  name?: string
  city?: string
  state?: string
  store_group_id?: string
  seller_id?: string | null
  supervisor_id?: string
  trade_marketing_coordinator_id?: string
  promoter_ids?: string[]
  seller_ids?: string[]
  promoters_company_id?: string | null
}

interface UpdateStoreResponse {
  id: string
  name: string
}

export async function updateStore({
  storeId,
  name,
  city,
  state,
  store_group_id,
  seller_id,
  supervisor_id,
  trade_marketing_coordinator_id,
  promoter_ids,
  seller_ids,
  promoters_company_id,
}: UpdateStoreRequest) {
  try {
    const { data } = await api.put<UpdateStoreResponse>('/stores', {
      storeId,
      name,
      city,
      state,
      store_group_id,
      seller_id,
      supervisor_id,
      trade_marketing_coordinator_id,
      promoter_ids,
      seller_ids,
      promoters_company_id,
    })

    return {
      store: data,
      success: true,
      message: 'Loja atualizada com sucesso!',
    }
  } catch (err) {
    if (err instanceof AxiosError) {
      return {
        success: false,
        message: err.response?.data.message || 'Não foi possível atualizar loja!',
      }
    }
  }
}

interface CreateStoreGroupRequest {
  name: string
  logo_url: string
}

interface CreateStoreGroupResponse {
  id: string
  name: string
}

export async function createStoreGroup({
  name,
  logo_url,
}: CreateStoreGroupRequest, token: string): Promise<CreateStoreGroupResponse | void> {
  try {
    const { data } = await api.post<CreateStoreGroupResponse>('/stores/groups', {
      name,
      logo_url,
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })

    return data
  } catch (err) {
    if (err instanceof AxiosError) {
      alert('Não foi possível criar grupo!')
    }
  }
}

interface UpdateStoreGroupRequest {
  id: string
  name?: string
  logo_url?: string
}

interface UpdateStoreGroupResponse {
  id: string
  name: string
}

export async function updateStoreGroup({
  id,
  name,
  logo_url,
}: UpdateStoreGroupRequest, token: string): Promise<UpdateStoreGroupResponse | void> {
  try {
    const { data } = await api.put<UpdateStoreGroupResponse>('/stores/groups', {
      id,
      name,
      logo_url,
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })

    return data
  } catch (err) {
    if (err instanceof AxiosError) {
      alert('Não foi possível salvar grupo!')
    }
  }
}

interface GetAllStoreGroupsResponse {
  storeGroups: {
    id: string
    name: string
    logo_url: string
  }[]
}

export async function getAllStoreGroups() {
  const { data } = await api.get<GetAllStoreGroupsResponse>('/stores/groups')

  return data
}

export async function getAllStoreGroupsByUser() {
  const { data } = await api.get<GetAllStoreGroupsResponse>('/stores/groups/user')

  return data
}

interface GetAllStoresResponse {
  stores: {
    id: string
    name: string
    state: string
    city: string
    group: {
      id: string
      name: string
    }
  }[]
}

export async function getAllStores() {
  const { data } = await api.get<GetAllStoresResponse>('/stores/all')

  return data
}

export async function getGroupStates(group_id?: string) {
  const { data } = await api.get<string[]>('/stores/groups/states', {
    params: {
      group_id,
    },
  })

  return data
}

interface GetGroupCitiesResponse {
  state: string
  city: string
  group: {
    id: string,
    name: string
  }
}

export async function getGroupCities(group_id?: string) {
  const { data } = await api.get<GetGroupCitiesResponse[]>('/stores/groups/cities', {
    params: {
      group_id,
    },
  })

  return data
}

interface GetStoresByCityResponse {
  stores: {
    id: string
    name: string
  }[]
}

export async function getStoresByCity(token: string, city: string) {
  const { data } = await api.get<GetStoresByCityResponse>('/stores/city', {
    params: {
      city,
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  return data
}

interface GetStoresByGroupResponse {
  stores: {
    id: string
    name: string
  }[]
}

export async function getStoresByGroup(token: string, group_id: string) {
  const { data } = await api.get<GetStoresByGroupResponse>('/stores/group', {
    params: {
      group_id,
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  return data
}

interface GetStoresByStateResponse {
  stores: {
    id: string
    name: string
  }[]
}

export async function getStoresByState(token: string, state: string) {
  const { data } = await api.get<GetStoresByStateResponse>('/stores/state', {
    params: {
      state,
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  return data
}

interface GetStoresGroupByIdResponse {
  storeGroup: {
    id: string
    name: string
    logo_url: string
  }
}

export async function getStoresGroupById(storeGroupId: string, token: string) {
  const { data } = await api.get<GetStoresGroupByIdResponse>(`/stores/groups/${storeGroupId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  return data
}
